import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Checkbox, ErrorBanner, Field, Input, Select, Skeleton, Switch, useApi, useApiQuery, useConfirm, useEngine, useToast } from '../../engine';
import { ApiError } from '../../engine/api/client';
import type { ScreenContext } from '../../engine/screens/types';

// "HaloPSA connection" — a section of the Settings screen (SettingsScreen.tsx is the engine
// exception; this is one of its cards). A tenant owner/admin connects the organisation to its
// HaloPSA without curl: the Halo sign-in strategy (POST/PATCH /v1/tenant-auth-strategies) and the
// live overlay switch (PUT/DELETE /v1/integrations/overlay), and the background mirror (its Halo
// service application, on/off, run now, per-entity progress: /v1/integrations/overlay/mirror).
// None is a ResourceDef (one halo row with a write-only secret; a tenant-keyed singleton), so there
// is no meta for AutoForm to render. Secrets are never read back — the API answers only `has_secret`
// / `has_service_secret`.

interface Strategy {
  id: string;
  kind: 'gotrue' | 'halo' | 'local';
  is_enabled: boolean;
  is_default: boolean;
  config: Record<string, unknown>;
  has_secret: boolean;
  updated_at: string;
}
interface Overlay {
  integration: string;
  enabled: boolean;
  mirror?: boolean;
  mirror_api_reads?: boolean;
  service_client_id?: string | null;
  has_service_secret?: boolean;
  mirror_requested_at?: string | null;
  cutover_requested_at?: string | null;
  cutover_accept_refused?: boolean;
  cutover_at?: string | null;
  cutover_error?: string | null;
  updated_at?: string;
}
interface MirrorCursor {
  entity: string;
  last_status: 'ok' | 'partial' | 'failed' | null;
  last_run_at: string | null;
  last_error: string | null;
  upserted: number;
  skipped: number;
  /** Records the last full sweep found deleted in the source (deactivated or removed here). */
  gone?: number;
}

const STRATEGIES_KEY = ['tenant-auth-strategies'] as const;
const OVERLAY_KEY = ['integrations', 'overlay'] as const;
const SESSION_KEY = ['integrations', 'overlay', 'session'] as const;
const MIRROR_KEY = ['integrations', 'overlay', 'mirror'] as const;
const ENTITY_LABELS: Record<string, string> = {
  ticket_status: 'Ticket statuses',
  ticket_type: 'Ticket types',
  ticket_priority: 'Priorities',
  queue: 'Teams',
  ticket_category: 'Categories',
  action_outcome: 'Outcomes',
  technician: 'Agents',
  client: 'Clients',
  site: 'Sites',
  contact: 'Contacts',
  ticket: 'Tickets and their conversations',
};
const ROLES = [
  { value: 'member', label: 'Member' },
  { value: 'admin', label: 'Admin' },
];

const errorDetail = (e: unknown) => (e instanceof ApiError ? e.detail || e.title : String(e));
const str = (v: unknown, fallback = '') => (typeof v === 'string' ? v : fallback);

/** Header block of an integration's detail page: the connection editor for the Halo row. */
export function IntegrationConnection({ row }: ScreenContext) {
  return row?.name === 'halo' ? <HaloConnectionCard /> : null;
}

/** Rendered only for a tenant owner/admin; the API refuses everyone else anyway. */
export function HaloConnectionCard() {
  const { principal } = useEngine();
  if (principal.tenantRole !== 'owner' && principal.tenantRole !== 'admin') return null;
  return <HaloConnection />;
}

function HaloConnection() {
  const api = useApi();
  const strategies = useApiQuery<{ data: Strategy[] }>(STRATEGIES_KEY, '/v1/tenant-auth-strategies');
  // 404 is the API's "not connected" answer, not an error.
  const overlay = useQuery({
    queryKey: OVERLAY_KEY,
    queryFn: async () => {
      try {
        return await api.request<Overlay>('/v1/integrations/overlay');
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
  });
  const session = useApiQuery<{ signed_in_with_halo: boolean }>(SESSION_KEY, '/v1/integrations/overlay/session');
  const halo = strategies.data?.data.find((s) => s.kind === 'halo');
  const connected = Boolean(overlay.data?.enabled);

  return (
    <section className="hf-card pad hf-stack" aria-labelledby="halo-heading">
      <div className="hf-caps" id="halo-heading">HaloPSA connection</div>
      {strategies.isLoading || overlay.isLoading ? (
        <Skeleton lines={4} />
      ) : strategies.error ? (
        <ErrorBanner error={strategies.error} />
      ) : (
        <>
          <p className="hf-muted" style={{ margin: 0, fontSize: 'var(--t-13)' }}>
            {connected ? 'Connected — tickets, clients and contacts are served live from Halo.' : halo ? 'Halo sign-in is set up; live data is off.' : 'Not connected.'}
          </p>
          {connected && session.data?.signed_in_with_halo === false && (
            <Banner tone="warn">You're signed in with email — sign out and sign in with SSO to see your Halo data.</Banner>
          )}
          <RedirectUri />
          <StrategyForm key={halo ? `${halo.id}:${halo.updated_at}` : 'new'} halo={halo} />
          <OverlaySwitch connected={connected} canConnect={Boolean(halo)} mirrorOn={Boolean(overlay.data?.mirror)} error={overlay.error} />
          <MirrorSection key={overlay.data?.updated_at ?? 'none'} overlay={overlay.data ?? null} canConnect={Boolean(halo)} />
        </>
      )}
    </section>
  );
}

function RedirectUri() {
  const toast = useToast();
  const uri = `${window.location.origin}/login/halo/callback`;
  return (
    <Field label="Redirect URI to register in Halo" id="halo-redirect" help="In Halo: Configuration → Integrations → HaloPSA API → Applications, authorization code, agent login.">
      <div className="hf-row" style={{ gap: 'var(--s-2)' }}>
        <Input id="halo-redirect" value={uri} readOnly className="hf-grow" />
        <Button size="sm" icon="copy" aria-label="Copy redirect URI" onClick={() => void navigator.clipboard?.writeText(uri).then(() => toast({ title: 'Redirect URI copied' }))}>
          Copy
        </Button>
      </div>
    </Field>
  );
}

function StrategyForm({ halo }: { halo: Strategy | undefined }) {
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const cfg = halo?.config ?? {};
  const [baseUrl, setBaseUrl] = useState(str(cfg.base_url));
  const [clientId, setClientId] = useState(str(cfg.client_id));
  const [secret, setSecret] = useState('');
  const [defaultRole, setDefaultRole] = useState(str(cfg.default_role, 'member'));
  const [scope, setScope] = useState(str(cfg.scope, 'all'));
  const [tenantName, setTenantName] = useState(str(cfg.tenant));
  const [isDefault, setIsDefault] = useState(halo?.is_default ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = baseUrl.trim() !== '' && clientId.trim() !== '' && (Boolean(halo?.has_secret) || secret !== '');

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const config = { ...cfg, base_url: baseUrl.trim(), client_id: clientId.trim(), scope: scope.trim() || 'all', default_role: defaultRole, tenant: tenantName.trim() || null };
    try {
      if (halo) {
        await api.request(`/v1/tenant-auth-strategies/${halo.id}`, { method: 'PATCH', body: { is_enabled: true, is_default: isDefault, config, ...(secret ? { secret } : {}) } });
      } else {
        await api.request('/v1/tenant-auth-strategies', { method: 'POST', body: { kind: 'halo', is_enabled: true, is_default: isDefault, config, secret } });
      }
      void qc.invalidateQueries({ queryKey: STRATEGIES_KEY });
      toast({ title: 'Halo sign-in saved' });
    } catch (err) {
      setError(errorDetail(err));
      toast({ kind: 'error', title: 'Could not save the Halo sign-in', detail: errorDetail(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="hf-stack" onSubmit={(e) => void save(e)} aria-label="Halo sign-in">
      {error && <Banner tone="error">{error}</Banner>}
      <Field label="Halo URL" id="halo-base-url" required help="Your Halo address, e.g. https://service.example.com (https, publicly reachable).">
        <Input id="halo-base-url" type="url" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://service.example.com" autoComplete="off" />
      </Field>
      <div className="hf-row" style={{ gap: 'var(--s-3)', flexWrap: 'wrap' }}>
        <Field label="Client id" id="halo-client-id" required>
          <Input id="halo-client-id" value={clientId} onChange={(e) => setClientId(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Client secret" id="halo-secret" required={!halo?.has_secret} help={halo?.has_secret ? 'Leave blank to keep the saved secret.' : undefined}>
          <Input id="halo-secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={halo?.has_secret ? '•••• saved' : ''} autoComplete="new-password" />
        </Field>
      </div>
      <div className="hf-row" style={{ gap: 'var(--s-3)', flexWrap: 'wrap' }}>
        <Field label="Role for new users" id="halo-default-role" help="Given to a Halo agent signing in for the first time.">
          <Select id="halo-default-role" value={defaultRole} onChange={(e) => setDefaultRole(e.target.value)} options={ROLES} />
        </Field>
        <Field label="Scope" id="halo-scope">
          <Input id="halo-scope" value={scope} onChange={(e) => setScope(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Tenant" id="halo-tenant" help="Only for Halo hosted at *.halopsa.com: your tenant name, sent with every sign-in. Leave blank for your own domain.">
          <Input id="halo-tenant" value={tenantName} onChange={(e) => setTenantName(e.target.value)} autoComplete="off" />
        </Field>
      </div>
      <Checkbox id="halo-is-default" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} label="Make Halo the sign-in for this organisation" />
      <div className="hf-row">
        <Button type="submit" variant="primary" busy={busy} disabled={!canSave}>
          {halo ? 'Save Halo sign-in' : 'Connect Halo sign-in'}
        </Button>
      </div>
    </form>
  );
}

function OverlaySwitch({ connected, canConnect, mirrorOn, error }: { connected: boolean; canConnect: boolean; mirrorOn: boolean; error: unknown }) {
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const toggle = async (on: boolean) => {
    setBusy(true);
    try {
      if (on) await api.request('/v1/integrations/overlay', { method: 'PUT', body: { integration: 'halo', enabled: true } });
      // With the mirror on, switching live data off keeps the row (and the mirror) and serves the mirrored tables.
      else if (mirrorOn) await api.request('/v1/integrations/overlay', { method: 'PUT', body: { integration: 'halo', enabled: false } });
      else await api.request('/v1/integrations/overlay', { method: 'DELETE' });
      void qc.invalidateQueries({ queryKey: OVERLAY_KEY });
      toast({ title: on ? 'Live Halo data on' : 'Live Halo data off' });
    } catch (err) {
      toast({ kind: 'error', title: 'Could not change the data source', detail: errorDetail(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hf-stack">
      {error ? <ErrorBanner error={error} /> : null}
      <Switch
        id="halo-overlay"
        checked={connected}
        disabled={busy || (!canConnect && !connected)}
        onChange={(e) => void toggle(e.target.checked)}
        label="Serve tickets, clients and contacts live from Halo"
      />
      <p className="hf-muted" style={{ margin: 0, fontSize: 'var(--t-13)' }}>
        When on, NovaPSA reads and writes those records in your Halo instead of its own tables; people then sign in with their work email and “Sign in with SSO”.
        {!canConnect && ' Save the Halo sign-in first.'}
      </p>
    </div>
  );
}

function ago(iso: string | null): string {
  if (!iso) return 'never';
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  return min < 1 ? 'just now' : min < 60 ? `${min} min ago` : min < 1440 ? `${Math.round(min / 60)} h ago` : `${Math.round(min / 1440)} d ago`;
}

function MirrorSection({ overlay, canConnect }: { overlay: Overlay | null; canConnect: boolean }) {
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const [clientId, setClientId] = useState(overlay?.service_client_id ?? '');
  const [secret, setSecret] = useState('');
  const [mirror, setMirror] = useState(Boolean(overlay?.mirror));
  const [apiReads, setApiReads] = useState(Boolean(overlay?.mirror_api_reads));
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const on = Boolean(overlay?.mirror);
  const status = useQuery({
    queryKey: MIRROR_KEY,
    queryFn: () => api.request<{ data: MirrorCursor[] }>('/v1/integrations/overlay/mirror'),
    enabled: on,
  });

  const canSave = canConnect && (!mirror || (clientId.trim() !== '' && (Boolean(overlay?.has_service_secret) || secret !== '')));

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.request('/v1/integrations/overlay', {
        method: 'PUT',
        body: { integration: 'halo', enabled: overlay?.enabled ?? false, mirror, mirror_api_reads: mirror && apiReads, service_client_id: clientId.trim() || null, ...(secret ? { service_secret: secret } : {}) },
      });
      void qc.invalidateQueries({ queryKey: OVERLAY_KEY });
      toast({ title: mirror ? 'Mirror on' : 'Mirror settings saved' });
    } catch (err) {
      setError(errorDetail(err));
      toast({ kind: 'error', title: 'Could not save the mirror', detail: errorDetail(err) });
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      await api.request('/v1/integrations/overlay/mirror/run', { method: 'POST' });
      void qc.invalidateQueries({ queryKey: OVERLAY_KEY });
      toast({ title: 'Mirror run requested', detail: 'The worker picks it up within a minute.' });
    } catch (err) {
      toast({ kind: 'error', title: 'Could not start the mirror', detail: errorDetail(err) });
    } finally {
      setRunning(false);
    }
  };

  return (
    <form className="hf-stack" onSubmit={(e) => void save(e)} aria-label="Halo mirror">
      <div className="hf-caps">Copy Halo into NovaPSA</div>
      <p className="hf-muted" style={{ margin: 0, fontSize: 'var(--t-13)' }}>
        In the background, NovaPSA copies your Halo statuses, teams, agents, clients, sites, contacts and tickets into its own tables, so moving off Halo
        later is a switch, not an import. It uses a separate Halo application (Client ID and Secret, Services).
      </p>
      {error && <Banner tone="error">{error}</Banner>}
      <div className="hf-row" style={{ gap: 'var(--s-3)', flexWrap: 'wrap' }}>
        <Field label="Service client id" id="halo-svc-client-id" required={mirror}>
          <Input id="halo-svc-client-id" value={clientId} onChange={(e) => setClientId(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Service client secret" id="halo-svc-secret" required={mirror && !overlay?.has_service_secret} help={overlay?.has_service_secret ? 'Leave blank to keep the saved secret.' : undefined}>
          <Input id="halo-svc-secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={overlay?.has_service_secret ? '•••• saved' : ''} autoComplete="new-password" />
        </Field>
      </div>
      <Switch id="halo-mirror" checked={mirror} disabled={!canConnect} onChange={(e) => setMirror(e.target.checked)} label="Keep a copy of Halo in NovaPSA" />
      <Checkbox
        id="halo-mirror-api-reads"
        checked={mirror && apiReads}
        disabled={!canConnect || !mirror}
        onChange={(e) => setApiReads(e.target.checked)}
        label="Let API keys read this copy (read-only; NovaPSA roles and key scopes apply, not Halo's permissions)"
      />
      <div className="hf-row" style={{ gap: 'var(--s-2)' }}>
        <Button type="submit" variant="primary" busy={busy} disabled={!canSave}>
          Save mirror settings
        </Button>
        {on && (
          <Button type="button" icon="refresh-cw" busy={running} disabled={Boolean(overlay?.mirror_requested_at)} onClick={() => void runNow()}>
            {overlay?.mirror_requested_at ? 'Run requested' : 'Run now'}
          </Button>
        )}
      </div>
      {on &&
        (status.isLoading ? (
          <Skeleton lines={3} />
        ) : status.error ? (
          <ErrorBanner error={status.error} />
        ) : status.data?.data.length ? (
          <ul className="hf-stack" aria-label="Mirror progress" style={{ margin: 0, paddingLeft: 0, listStyle: 'none', fontSize: 'var(--t-13)' }}>
            {status.data.data
              .slice()
              .sort((a, b) => Object.keys(ENTITY_LABELS).indexOf(a.entity) - Object.keys(ENTITY_LABELS).indexOf(b.entity))
              .map((r) => (
                <li key={r.entity}>
                  <strong>{ENTITY_LABELS[r.entity] ?? r.entity}</strong> — {r.last_status ?? 'not run'} · {r.upserted} copied
                  {r.skipped ? ` · ${r.skipped} refused` : ''}
                  {r.gone ? ` · ${r.gone} deleted in Halo` : ''} · {ago(r.last_run_at)}
                  {r.last_error && (
                    <div className="hf-muted" style={{ whiteSpace: 'pre-wrap' }}>
                      {r.last_error}
                    </div>
                  )}
                </li>
              ))}
          </ul>
        ) : (
          <p className="hf-muted" style={{ margin: 0, fontSize: 'var(--t-13)' }}>
            No run yet.
          </p>
        ))}
      <CutoverSection overlay={overlay} />
    </form>
  );
}

/**
 * Moving off Halo: a final full sync, then the worker switches live data and the mirror off, so
 * NovaPSA becomes the system of record (POST/DELETE /v1/integrations/overlay/cut-over). Step-up.
 */
function CutoverSection({ overlay }: { overlay: Overlay | null }) {
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [acceptRefused, setAcceptRefused] = useState(false);
  const [busy, setBusy] = useState(false);
  if (overlay?.cutover_at) {
    return <Banner tone="pos">Moved off Halo on {new Date(overlay.cutover_at).toLocaleString()}. NovaPSA is the system of record.</Banner>;
  }
  if (!overlay?.enabled || !overlay.mirror) return null;
  const pending = Boolean(overlay.cutover_requested_at);

  const call = async (method: 'POST' | 'DELETE') => {
    if (method === 'POST') {
      const ok = await confirm({
        title: 'Move off Halo?',
        body: 'NovaPSA reads everything from Halo one last time. When that sync is clean it stops reading Halo: live data and the mirror switch off, and from then on your team works in NovaPSA. Stop changing records in Halo first — changes made after the final sync are not copied.',
        confirmLabel: 'Move off Halo',
        danger: true,
      });
      if (ok === false) return;
    }
    setBusy(true);
    try {
      await api.request('/v1/integrations/overlay/cut-over', method === 'POST' ? { method, body: { accept_refused: acceptRefused } } : { method });
      void qc.invalidateQueries({ queryKey: OVERLAY_KEY });
      toast({ title: method === 'POST' ? 'Cut-over requested' : 'Cut-over withdrawn', detail: method === 'POST' ? 'The final sync starts within a minute.' : undefined });
    } catch (err) {
      toast({ kind: 'error', title: method === 'POST' ? 'Could not request the cut-over' : 'Could not withdraw the cut-over', detail: errorDetail(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hf-stack" aria-label="Move off Halo" role="group">
      <div className="hf-caps">Move off Halo</div>
      {pending ? (
        <>
          <Banner tone={overlay.cutover_error ? 'warn' : 'info'}>
            Final sync requested {ago(overlay.cutover_requested_at ?? null)}
            {overlay.cutover_accept_refused ? ' (records that cannot be copied are left behind)' : ''}.
            {overlay.cutover_error ? ` Not yet: ${overlay.cutover_error}.` : ' NovaPSA switches over once it completes cleanly.'}
          </Banner>
          <div>
            <Button type="button" busy={busy} onClick={() => void call('DELETE')}>
              Withdraw cut-over
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="hf-muted" style={{ margin: 0, fontSize: 'var(--t-13)' }}>
            When the copy is complete, switch to NovaPSA: one last full sync, then NovaPSA stops reading Halo. Needs a second factor.
          </p>
          <Checkbox id="halo-cutover-accept" checked={acceptRefused} onChange={(e) => setAcceptRefused(e.target.checked)} label="Switch even if some records cannot be copied" />
          <div>
            <Button type="button" variant="danger" busy={busy} onClick={() => void call('POST')}>
              Move off Halo…
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
